import React, { useEffect, useState, useCallback } from "react";
import { Search, Users, Crown, Wallet, X, PlusCircle, MinusCircle, ShieldCheck, Activity, RefreshCw, Ban, Trash2, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { VipBadge } from "./VipTierCard";
import { ModalPortal } from "./ModalPortal";

const TIER_OPTIONS = [
    { slug: "all", label: "All tiers" },
    { slug: "none", label: "Researcher" },
    { slug: "bronze", label: "Bronze" },
    { slug: "silver", label: "Silver" },
    { slug: "gold", label: "Gold" },
    { slug: "platinum", label: "Platinum" },
    { slug: "diamond", label: "Diamond" },
];

const TIER_OVERRIDE_SLUGS = ["none", "bronze", "silver", "gold", "platinum", "diamond"];

const PAGE = 25;

export const AdminCustomersPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [stats, setStats] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [search, setSearch] = useState("");
    const [tier, setTier] = useState("all");
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [list, statsRes] = await Promise.all([
                api.get("/admin/customers", { params: { search, tier, limit: PAGE, skip }, headers }),
                api.get("/admin/customers/stats/overview", { headers }),
            ]);
            setCustomers(list.data.customers);
            setTotal(list.data.total);
            setStats(statsRes.data);
        } catch {
            toast.error("Could not load customers");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, tier, skip, token]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-5" data-testid="admin-customers-panel">
            {/* Stats overview */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard icon={Users} label="Total customers" value={stats.total_customers} />
                    <StatCard icon={Crown} label="VIP members" value={
                        Object.entries(stats.tier_breakdown)
                            .filter(([k]) => k !== "none")
                            .reduce((a, [, v]) => a + v, 0)
                    } />
                    <StatCard icon={Wallet} label="Credit outstanding" value={`A$${Number(stats.outstanding_store_credit).toFixed(2)}`} />
                    <StatCard icon={ShieldCheck} label="Gold + Platinum + Diamond" value={(stats.tier_breakdown.gold || 0) + (stats.tier_breakdown.platinum || 0) + (stats.tier_breakdown.diamond || 0)} />
                </div>
            )}

            {/* Filters */}
            <div className="glass rounded-2xl p-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <input
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
                        placeholder="Search by name, email, or user ID…"
                        className="w-full pl-9 pr-3 py-2 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="customer-search-input"
                    />
                </div>
                <select
                    value={tier}
                    onChange={(e) => { setTier(e.target.value); setSkip(0); }}
                    className="px-3 py-2 bg-black/40 border border-white/10 rounded-full text-xs font-mono uppercase tracking-widest text-white focus:outline-none focus:border-[var(--gold)]"
                    data-testid="customer-tier-filter"
                >
                    {TIER_OPTIONS.map((o) => <option key={o.slug} value={o.slug}>{o.label}</option>)}
                </select>
                <button onClick={load} className="btn-neon" data-testid="customer-refresh">
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            {/* List */}
            <div className="glass-elevated rounded-2xl overflow-hidden" data-testid="customer-list">
                <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_120px] gap-3 px-5 py-3 border-b border-white/5 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                    <div>Customer</div><div>Tier</div><div>Lifetime spend</div><div>Store credit</div><div className="text-right">Action</div>
                </div>
                {loading && <div className="text-center text-[var(--text-secondary)] py-10 text-sm">Loading…</div>}
                {!loading && customers.length === 0 && (
                    <div className="text-center text-[var(--text-secondary)] py-12 text-sm">No customers match.</div>
                )}
                {customers.map((c) => (
                    <button
                        key={c.user_id}
                        onClick={() => setSelected(c.user_id)}
                        className="w-full text-left grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_120px] gap-3 px-5 py-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                        data-testid={`customer-row-${c.user_id}`}
                    >
                        <div className="min-w-0">
                            <p className="text-sm text-white font-bold truncate">{c.name || c.email.split("@")[0]}</p>
                            <p className="text-xs text-[var(--text-muted)] font-mono truncate">{c.email}</p>
                        </div>
                        <div className="flex items-center"><VipBadge tier={c.vip_tier} size="sm" /></div>
                        <div className="text-sm font-mono text-[var(--gold)]">A${Number(c.lifetime_spend).toFixed(2)}</div>
                        <div className="text-sm font-mono text-emerald-300">A${Number(c.store_credit_balance).toFixed(2)}</div>
                        <div className="text-xs font-mono uppercase tracking-widest text-[var(--gold)] md:text-right">Manage →</div>
                    </button>
                ))}
                {/* Pagination */}
                <div className="flex items-center justify-between px-5 py-3 text-xs font-mono text-[var(--text-muted)]">
                    <span>{Math.min(skip + 1, total)}–{Math.min(skip + customers.length, total)} of {total}</span>
                    <div className="flex gap-2">
                        <button onClick={() => setSkip(Math.max(0, skip - PAGE))} disabled={skip === 0}
                                className="px-3 py-1.5 rounded-full border border-white/10 disabled:opacity-40 hover:border-white/30"
                                data-testid="customers-prev">Prev</button>
                        <button onClick={() => setSkip(skip + PAGE)} disabled={skip + PAGE >= total}
                                className="px-3 py-1.5 rounded-full border border-white/10 disabled:opacity-40 hover:border-white/30"
                                data-testid="customers-next">Next</button>
                    </div>
                </div>
            </div>

            {selected && (
                <CustomerDetailDrawer
                    userId={selected}
                    token={token}
                    onClose={() => setSelected(null)}
                    onSaved={() => load()}
                />
            )}
        </div>
    );
};

const StatCard = ({ icon: Icon, label, value }) => (
    <div className="glass rounded-xl p-4">
        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 flex items-center gap-1.5">
            <Icon size={12} /> {label}
        </p>
        <p className="font-display text-2xl font-extrabold text-white italic tracking-tighter">{value}</p>
    </div>
);

const CustomerDetailDrawer = ({ userId, token, onClose, onSaved }) => {
    const headers = { "X-Admin-Token": token };
    const [detail, setDetail] = useState(null);
    const [creditForm, setCreditForm] = useState({ amount: "", kind: "goodwill", reason: "" });
    const [creditMode, setCreditMode] = useState("add");
    const [busy, setBusy] = useState(false);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/customers/${userId}`, { headers });
            setDetail(data);
        } catch {
            toast.error("Could not load customer");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, token]);

    useEffect(() => { reload(); }, [reload]);

    const adjustCredit = async () => {
        const amount = parseFloat(creditForm.amount);
        if (!amount || amount <= 0) return toast.error("Enter an amount");
        if (!creditForm.reason.trim()) return toast.error("Reason is required");
        setBusy(true);
        try {
            const path = creditMode === "add" ? "credit/add" : "credit/remove";
            const body = creditMode === "add"
                ? { amount, kind: creditForm.kind, reason: creditForm.reason }
                : { amount, reason: creditForm.reason };
            await api.post(`/admin/customers/${userId}/${path}`, body, { headers });
            toast.success(creditMode === "add" ? "Credit issued" : "Credit removed");
            setCreditForm({ amount: "", kind: "goodwill", reason: "" });
            await reload();
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    const setTier = async (slug) => {
        setBusy(true);
        try {
            await api.post(`/admin/customers/${userId}/tier`,
                { tier: slug === "auto" ? null : slug }, { headers });
            toast.success("Tier updated");
            await reload();
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    const [confirmAction, setConfirmAction] = useState(null); // 'suspend' | 'reactivate' | 'delete'
    const [suspendReason, setSuspendReason] = useState("");

    const doSuspend = async (suspend) => {
        setBusy(true);
        try {
            await api.post(`/admin/customers/${userId}/suspend`,
                { suspend, reason: suspend ? suspendReason : undefined }, { headers });
            toast.success(suspend ? "Account suspended" : "Account reactivated");
            setConfirmAction(null);
            setSuspendReason("");
            await reload();
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    const doHardDelete = async () => {
        setBusy(true);
        try {
            await api.delete(`/admin/customers/${userId}`, { headers });
            toast.success("Account deleted");
            onSaved?.();
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
        setBusy(false);
    };

    if (!detail) {
        return (
            <ModalPortal>
            <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm">
                <div className="inline-block w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
            </div>
            </ModalPortal>
        );
    }

    const c = detail.customer;
    return (
        <ModalPortal>
        <div className="fixed inset-0 z-[90] flex items-stretch justify-end bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl glass-elevated overflow-y-auto p-6 sm:p-8"
                data-testid="customer-detail-drawer"
            >
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Customer</p>
                        <h2 className="font-display text-2xl font-extrabold text-white italic tracking-tighter">{c.name || c.email.split("@")[0]}</h2>
                        <p className="text-xs text-[var(--text-muted)] font-mono mt-1">{c.email}</p>
                        <p className="text-[10px] text-[var(--text-muted)] font-mono mt-1">{c.user_id}</p>
                    </div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-white" data-testid="close-customer-drawer">
                        <X size={20} />
                    </button>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                    <SummaryStat label="Lifetime spend" value={`A$${Number(c.lifetime_spend).toFixed(2)}`} highlight />
                    <SummaryStat label="Paid orders" value={detail.paid_order_count} />
                    <SummaryStat label="Store credit" value={`A$${Number(c.store_credit_balance).toFixed(2)}`} />
                </div>

                {/* VIP tier control */}
                <section className="glass rounded-2xl p-5 mb-5">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-lg font-bold text-white">VIP tier</h3>
                        <VipBadge tier={c.vip_tier} />
                    </div>
                    <p className="text-xs text-[var(--text-muted)] mb-3">
                        Natural tier from spend: <span className="text-white">{detail.natural_tier || "none"}</span>
                        {detail.manual_override && <> · Admin override: <span className="text-[var(--gold)]">{detail.manual_override}</span></>}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {TIER_OVERRIDE_SLUGS.map((slug) => (
                            <button
                                key={slug}
                                disabled={busy}
                                onClick={() => setTier(slug)}
                                className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border ${
                                    detail.manual_override === slug
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/30"
                                } disabled:opacity-40`}
                                data-testid={`set-tier-${slug}`}
                            >
                                {slug}
                            </button>
                        ))}
                        <button
                            disabled={busy || !detail.manual_override}
                            onClick={() => setTier("auto")}
                            className="px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border border-white/10 text-[var(--text-secondary)] hover:border-white/30 disabled:opacity-40 inline-flex items-center gap-1"
                            data-testid="set-tier-auto"
                        >
                            <Activity size={12} /> Auto
                        </button>
                    </div>
                </section>

                {/* Store credit */}
                <section className="glass rounded-2xl p-5 mb-5">
                    <h3 className="font-display text-lg font-bold text-white mb-3">Store credit</h3>
                    <div className="flex items-center gap-2 mb-3">
                        <button
                            onClick={() => setCreditMode("add")}
                            className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-1 ${
                                creditMode === "add"
                                    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                                    : "border-white/10 text-[var(--text-secondary)]"
                            }`}
                            data-testid="credit-mode-add"
                        >
                            <PlusCircle size={12} /> Add credit
                        </button>
                        <button
                            onClick={() => setCreditMode("remove")}
                            className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-1 ${
                                creditMode === "remove"
                                    ? "border-red-500/40 text-red-300 bg-red-500/10"
                                    : "border-white/10 text-[var(--text-secondary)]"
                            }`}
                            data-testid="credit-mode-remove"
                        >
                            <MinusCircle size={12} /> Remove credit
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <input
                            type="number" min="0.01" step="0.01"
                            value={creditForm.amount}
                            onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                            placeholder="Amount (AUD)"
                            className="px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                            data-testid="credit-amount-input"
                        />
                        {creditMode === "add" && (
                            <select
                                value={creditForm.kind}
                                onChange={(e) => setCreditForm({ ...creditForm, kind: e.target.value })}
                                className="px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-xs font-mono uppercase tracking-widest text-white focus:outline-none focus:border-[var(--gold)]"
                                data-testid="credit-kind-select"
                            >
                                <option value="goodwill">Goodwill</option>
                                <option value="promo">Promo</option>
                                <option value="refund">Refund</option>
                                <option value="adjustment">Adjustment</option>
                            </select>
                        )}
                        <input
                            value={creditForm.reason}
                            onChange={(e) => setCreditForm({ ...creditForm, reason: e.target.value })}
                            placeholder="Reason / note"
                            className={`px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)] ${creditMode === "remove" ? "sm:col-span-2" : ""}`}
                            data-testid="credit-reason-input"
                        />
                    </div>
                    <button
                        onClick={adjustCredit}
                        disabled={busy}
                        className="btn-solid mt-3 disabled:opacity-50"
                        data-testid="credit-submit"
                    >
                        {creditMode === "add" ? "Issue credit" : "Remove credit"}
                    </button>
                </section>

                {/* Credit ledger */}
                <section className="glass rounded-2xl p-5">
                    <h3 className="font-display text-lg font-bold text-white mb-3">Credit history</h3>
                    {(detail.credit_history || []).length === 0 && (
                        <p className="text-sm text-[var(--text-secondary)]">No activity yet.</p>
                    )}
                    <div className="space-y-2">
                        {(detail.credit_history || []).map((e) => (
                            <div key={e.id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-black/40 border border-white/5">
                                <div className="min-w-0">
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                        {e.kind} · {new Date(e.created_at).toLocaleDateString("en-AU")}
                                    </p>
                                    <p className="text-sm text-white truncate">{e.reason}</p>
                                </div>
                                <div className="text-right">
                                    <p className={`font-mono font-bold ${e.direction === "credit" ? "text-emerald-300" : "text-red-300"}`}>
                                        {e.direction === "credit" ? "+" : "-"}A${Number(e.amount).toFixed(2)}
                                    </p>
                                    <p className="text-[10px] text-[var(--text-muted)]">Bal A${Number(e.balance_after).toFixed(2)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Danger zone: suspend + delete */}
                <section className="glass rounded-2xl p-5 border border-red-500/25 mt-4" data-testid="customer-danger-zone">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle size={16} className="text-red-300" />
                        <h3 className="font-display text-lg font-bold text-white">Danger zone</h3>
                        {detail.is_suspended && (
                            <span className="text-[10px] font-mono uppercase tracking-widest text-red-300 bg-red-500/15 border border-red-500/40 px-2 py-0.5 rounded-full">
                                Currently suspended
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-[var(--text-muted)] mb-4">
                        Suspend prevents login but preserves order history + credit ledger. Permanent delete removes the profile + wishlists + credit ledger and anonymises past orders.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {detail.is_suspended ? (
                            <button
                                onClick={() => setConfirmAction("reactivate")}
                                disabled={busy}
                                className="px-4 py-2 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                data-testid="customer-reactivate"
                            >
                                <RotateCcw size={12} /> Reactivate account
                            </button>
                        ) : (
                            <button
                                onClick={() => setConfirmAction("suspend")}
                                disabled={busy}
                                className="px-4 py-2 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                data-testid="customer-suspend"
                            >
                                <Ban size={12} /> Suspend account
                            </button>
                        )}
                        <button
                            onClick={() => setConfirmAction("delete")}
                            disabled={busy}
                            className="px-4 py-2 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                            data-testid="customer-delete"
                        >
                            <Trash2 size={12} /> Permanently delete
                        </button>
                    </div>
                    {detail.is_suspended && detail.suspension_reason && (
                        <p className="text-[10px] text-[var(--text-muted)] font-mono mt-3 italic">
                            Reason: {detail.suspension_reason}
                        </p>
                    )}
                </section>

                {/* Confirm popup for danger actions */}
                {confirmAction && (
                    <div
                        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={() => setConfirmAction(null)}
                        data-testid="customer-confirm-modal"
                    >
                        <div
                            className="glass-elevated rounded-2xl p-6 max-w-md w-full border border-red-500/30"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center">
                                    <AlertTriangle size={18} className="text-red-300" />
                                </div>
                                <h3 className="font-display text-lg font-bold text-white">
                                    {confirmAction === "suspend" && "Suspend account?"}
                                    {confirmAction === "reactivate" && "Reactivate account?"}
                                    {confirmAction === "delete" && "Permanently delete?"}
                                </h3>
                            </div>
                            <p className="text-sm text-[var(--text-secondary)] mb-3">
                                <span className="text-white font-semibold">{c.name || c.email}</span>
                                {confirmAction === "suspend" && " will be blocked from logging in. Order history + credit are preserved and can be reactivated later."}
                                {confirmAction === "reactivate" && " will regain full access to their account and can log in again."}
                                {confirmAction === "delete" && " will be permanently removed. All personal data (wishlist, credit ledger, tracking) is wiped. Past orders are kept but anonymised for tax records."}
                            </p>
                            {confirmAction === "suspend" && (
                                <input
                                    value={suspendReason}
                                    onChange={(e) => setSuspendReason(e.target.value)}
                                    placeholder="Optional reason (visible to admins only)"
                                    className="w-full px-3 py-2 mb-4 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                    data-testid="customer-suspend-reason"
                                />
                            )}
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => { setConfirmAction(null); setSuspendReason(""); }}
                                    className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:border-white/25"
                                    data-testid="customer-confirm-cancel"
                                >
                                    Cancel
                                </button>
                                {confirmAction === "suspend" && (
                                    <button
                                        onClick={() => doSuspend(true)}
                                        disabled={busy}
                                        className="px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                        data-testid="customer-confirm-suspend"
                                    >
                                        <Ban size={12} /> Suspend
                                    </button>
                                )}
                                {confirmAction === "reactivate" && (
                                    <button
                                        onClick={() => doSuspend(false)}
                                        disabled={busy}
                                        className="px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/30 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                        data-testid="customer-confirm-reactivate"
                                    >
                                        <RotateCcw size={12} /> Reactivate
                                    </button>
                                )}
                                {confirmAction === "delete" && (
                                    <button
                                        onClick={doHardDelete}
                                        disabled={busy}
                                        className="px-4 py-2 rounded-full bg-red-500/20 border border-red-500/50 text-red-200 hover:bg-red-500/30 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                        data-testid="customer-confirm-delete"
                                    >
                                        <Trash2 size={12} /> Delete forever
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </ModalPortal>
    );
};

const SummaryStat = ({ label, value, highlight = false }) => (
    <div className={`rounded-xl p-3 ${highlight ? "bg-[rgba(233,188,11,0.06)] border border-[var(--gold)]/30" : "bg-black/40 border border-white/5"}`}>
        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">{label}</p>
        <p className={`font-mono font-bold text-base ${highlight ? "text-[var(--gold)]" : "text-white"}`}>{value}</p>
    </div>
);
